import { StatusChip } from '@/components/status-chip'
import { attachmentChipStatuses } from '@/constants/attachment-chip-statuses'
import { Pagination } from '@/shared/components/pagination'
import { PdfPreviewModal } from '@/shared/components/pdf-preview-modal'
import { Typography } from '@material-tailwind/react'
import {
  Body,
  Cell,
  Header,
  HeaderCell,
  HeaderRow,
  Row,
  Table
} from '@table-library/react-table-library/table'
import { useTranslation } from 'react-i18next'
import { FiEye, FiUpload } from 'react-icons/fi'
import { Link, generatePath, useSearchParams, useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'

import { usePagination } from '@table-library/react-table-library/pagination'
import { useTheme } from '@table-library/react-table-library/theme'
import { useGetAttachmentsQuery } from '@/store/services'
import { LoadingRow } from '@/shared/components/tables'
import { useState } from 'react'
import { ATTACHMENT_STATUSES } from '@/constants'
import {
  getReplacementStatusChipValue,
  getVisibleAttachmentComment
} from '@/utils/attachment-replacement'

export const AttachmentTable = ({ skip }) => {
  const isPdf = (contentType) => contentType === 'application/pdf'
  const formatDateDMY = (s) => {
    if (!s) return '-'
    const [y, m, d] = s.split('-')
    return `${d}-${m}-${y}`
  }
  const [pdfModal, setPdfModal] = useState({
    open: false,
    url: ""
  })
  const [searchParams, setSearchParams] = useSearchParams()
  const PER_PAGE = searchParams.get('per_page') || 10
  const INITIAL_PAGE = 1
  const { t } = useTranslation()
  const currentUrlParams =  useLocation().search;

  const {
    data: { attachments = [], meta = {} } = {},
    isFetching: isFetchingAttachments
  } = useGetAttachmentsQuery(location.search, { skip: isEmpty(location.search) || skip })

  const theme = useTheme({
    Table: `
        --data-table-library_grid-template-columns: 0.5fr 2.5fr 2.5fr 2.5fr 0.5fr 3fr 0.3fr 0.3fr 0.3fr;
      `,

    BaseCell: `
      &:nth-of-type(9) {
        right: 0px;
      }
    `
  })

  const nodes = { nodes: attachments }

  const onPaginationChange = (_, state) => {
    const page = state.page + 1 || INITIAL_PAGE
    const perPage = searchParams.get('per_page') || PER_PAGE

    searchParams.set('page', page)
    searchParams.set('per_page', perPage)
    setSearchParams(searchParams)
  }

  const pagination = usePagination(
    nodes,
    {
      state: {
        page: searchParams.get('page') - 1 || 0,
        size: searchParams.get('per_page') || 10
      },
      onChange: onPaginationChange
    },
    {
      isServer: true
    }
  )

  return (

    <div className="flex flex-col justify-between">
      <PdfPreviewModal {...pdfModal} handler={() => setPdfModal(
        {
          open: false,
          url: ""
        }
      )} />
      <Table
        data={nodes}
        pagination={pagination}
        theme={theme}
        layout={{ custom: true, fixedHeader: true }}
      >
        {(tableList) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCell>Módulo</HeaderCell>
                <HeaderCell>Documento</HeaderCell>
                <HeaderCell>Información asociada</HeaderCell>
                <HeaderCell>{t('contract.one')}</HeaderCell>
                <HeaderCell>Estado</HeaderCell>
                <HeaderCell>Comentarios</HeaderCell>
                <HeaderCell>Periodo</HeaderCell>
                <HeaderCell>Vencimiento</HeaderCell>
                <HeaderCell pinRight>Acciones</HeaderCell>
              </HeaderRow>
            </Header>

            <Body>
              {
                isFetchingAttachments && <LoadingRow gridColumnEnd={9} />
              }
              {
                tableList.map((attachment) => {
                  const replacementStatusChipValue = getReplacementStatusChipValue(attachment)
                  const recurrence =
                    attachment?.attachmentType?.recurrence ||
                    attachment?.attachment_type?.recurrence ||
                    attachment?.recurrence

                  const hideUpload =
                    attachment?.openReplacementCandidate?.id ||
                    attachment?.status === ATTACHMENT_STATUSES.notApplicable ||
                    (
                      attachment?.status === ATTACHMENT_STATUSES.approved &&
                      ['termination', 'monthly'].includes(recurrence)
                    )

                  return (
                    <Row key={attachment.id} item={attachment}>
                      <Cell>{attachment.module}</Cell>
                      <Cell>
                        <div className='overflow-hidden text-wrap'>
                          {attachment.attachmentType.name}
                        </div>
                      </Cell>
                      <Cell style={{ maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <div className='flex flex-col overflow-hidden break-words whitespace-normal leading-snug'>
                          <span className='text-sm font-normal text-blue-gray-800'>
                            {attachment.associatedInfo?.name}
                          </span>
                          <span className='text-sm font-normal text-blue-gray-800'>
                            {attachment.associatedInfo?.documentNumber}
                          </span>
                        </div>
                      </Cell>
                      <Cell style={{ maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <div className='overflow-hidden break-words whitespace-normal leading-snug text-blue-gray-800 text-sm'>
                          {attachment.associatedInfo?.contractorContractId} - {attachment.associatedInfo?.contractName} <br />
                          {attachment.associatedInfo?.companyName}
                        </div>
                      </Cell>
                      <Cell>
                        <div className="flex flex-col gap-1">
                          <StatusChip
                            options={attachmentChipStatuses}
                            value={attachment.status}
                          />
                          {attachment.aboutToExpire && (
                            <StatusChip
                              options={attachmentChipStatuses}
                              value="about_to_expire"
                            />
                          )}
                          {replacementStatusChipValue && (
                            <StatusChip
                              options={attachmentChipStatuses}
                              value={replacementStatusChipValue}
                            />
                          )}
                        </div>
                      </Cell>
                      <Cell class='px-4 py-2 mt-2'>{getVisibleAttachmentComment(attachment)}</Cell>
                      <Cell>{attachment.period}</Cell>
                      <Cell>{attachment.expirationDate ? formatDateDMY(attachment.expirationDate) : "-"}</Cell>

                      <Cell pinRight>
                        <div className='flex gap-2 mt-1'>
                          {!hideUpload && (
                            <Link
                              to={generatePath(
                                `/attachments/:id/upload_attachments${currentUrlParams}`,
                                { id: attachment.id }
                              )}
                            >
                              <FiUpload />
                            </Link>
                          )}
                          {attachment?.documentInfo?.url &&

                            <FiEye className='cursor-pointer' onClick={() => {
                              isPdf(attachment?.documentInfo?.contentType) ?
                                setPdfModal({
                                  open: true,
                                  url: attachment.documentInfo?.url
                                })
                                : window.open(attachment?.documentInfo?.url, '_blank').focus();
                            }} />
                          }
                        </div>
                      </Cell>
                    </Row>
                  )
                })
              }
            </Body>
          </>
        )}
      </Table>
      <Pagination
        onPageChange={({ selected }) => pagination.fns.onSetPage(selected)}
        page={pagination.state.page}
        totalPage={meta?.pagination?.total_pages}
      />
    </div>
  )
}
